import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FaUser,
  FaBriefcase,
  FaFilePdf,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaSearch,
  FaDownload,
  FaSyncAlt,
  FaGraduationCap,
  FaRupeeSign,
  FaTimes,
  FaDatabase,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import './SuperAdmin.css';

const GOOGLE_SHEETS_URL = 'https://script.google.com/macros/s/AKfycbxJx99FRIBAvkF_MZxONTBECGIXDBu1cvDyvz03lg7RCsikvm2oDrCPBCSkdSlD8MvW/exec';

// Realistic mockup data for demo mode
const MOCKUP_CANDIDATES = [
  {
    "Timestamp": "2026-05-22T08:34:12.124Z",
    "firstName": "Arjun",
    "lastName": "Sharma",
    "email": "arjun.sharma@example.com",
    "contact": "+91 98765 43210",
    "gender": "Male",
    "yearOfGraduation": "2022",
    "experienceInYears": "4",
    "currentEmployer": "TCS Ltd",
    "currentCTC": "6.5 LPA",
    "expectedCTC": "9.0 LPA",
    "noticePeriod": "30 Days",
    "skillSet": "React, JavaScript, Node.js, SQL, AWS",
    "source": "LinkedIn",
    "currentLocation": "Chennai",
    "preferredLocation": "Coimbatore",
    "fileName": "Arjun_Sharma_Resume.pdf",
    "resumeUrl": "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf"
  },
  {
    "Timestamp": "2026-05-22T07:12:45.654Z",
    "firstName": "Priya",
    "lastName": "Nair",
    "email": "priya.nair@example.com",
    "contact": "+91 87654 32109",
    "gender": "Female",
    "yearOfGraduation": "2020",
    "experienceInYears": "6",
    "currentEmployer": "Infosys",
    "currentCTC": "8.0 LPA",
    "expectedCTC": "11.5 LPA",
    "noticePeriod": "Immediate",
    "skillSet": "TallyPrime, ERP Solutions, GST Compliance, Tax Filing, Excel",
    "source": "Naukri",
    "currentLocation": "Bangalore",
    "preferredLocation": "Chennai",
    "fileName": "Priya_Nair_Finance_Expert.pdf",
    "resumeUrl": "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf"
  },
  {
    "Timestamp": "2026-05-21T14:22:01.345Z",
    "firstName": "Rahul",
    "lastName": "Varma",
    "email": "rahul.varma@example.com",
    "contact": "+91 76543 21098",
    "gender": "Male",
    "yearOfGraduation": "2024",
    "experienceInYears": "1",
    "currentEmployer": "Fresh Graduate",
    "currentCTC": "N/A",
    "expectedCTC": "4.5 LPA",
    "noticePeriod": "Immediate",
    "skillSet": "HTML5, CSS3, JavaScript, Bootstrap, React, Git",
    "source": "College Placement",
    "currentLocation": "Coimbatore",
    "preferredLocation": "Coimbatore",
    "fileName": "Rahul_Varma_Resume.pdf",
    "resumeUrl": "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf"
  },
  {
    "Timestamp": "2026-05-21T09:45:12.789Z",
    "firstName": "Sneha",
    "lastName": "Reddy",
    "email": "sneha.reddy@example.com",
    "contact": "+91 99445 56677",
    "gender": "Female",
    "yearOfGraduation": "2018",
    "experienceInYears": "8",
    "currentEmployer": "Cognizant",
    "currentCTC": "12.0 LPA",
    "expectedCTC": "16.0 LPA",
    "noticePeriod": "90 Days",
    "skillSet": "Network Routing, Cisco Networking, Cybersecurity, Cloud Infrastructure, Docker",
    "source": "Referral",
    "currentLocation": "Hyderabad",
    "preferredLocation": "Chennai",
    "fileName": "Sneha_Reddy_Network_Architect.pdf",
    "resumeUrl": "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf"
  },
  {
    "Timestamp": "2026-05-20T11:05:33.210Z",
    "firstName": "Karthik",
    "lastName": "Rao",
    "email": "karthik.rao@example.com",
    "contact": "+91 91234 56789",
    "gender": "Male",
    "yearOfGraduation": "2021",
    "experienceInYears": "5",
    "currentEmployer": "Wipro Technologies",
    "currentCTC": "7.2 LPA",
    "expectedCTC": "9.5 LPA",
    "noticePeriod": "60 Days",
    "skillSet": "React Native, Mobile App Development, iOS, Android, Flutter, Firebase",
    "source": "Indeed",
    "currentLocation": "Madurai",
    "preferredLocation": "Coimbatore",
    "fileName": "Karthik_Rao_Mobile_Dev.pdf",
    "resumeUrl": "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf"
  }
];

const SuperAdmin = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const location = useLocation();
  const [experienceFilter, setExperienceFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [hasError, setHasError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Reset pagination to page 1 when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, experienceFilter, locationFilter]);

  // Fetch candidates from Google Sheet Web App
  const fetchCandidates = async (forceDemo = false) => {
    setLoading(true);
    setHasError(false);
    
    if (forceDemo) {
      setTimeout(() => {
        setCandidates(MOCKUP_CANDIDATES);
        setLoading(false);
        setIsDemoMode(true);
        toast.info("Showing Interactive Demo Data");
      }, 800);
      return;
    }

    try {
      const response = await fetch(GOOGLE_SHEETS_URL);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // If the response is not an array (e.g. error page or HTML error text)
      if (!Array.isArray(data)) {
        if (data && data.error) {
          throw new Error(data.error);
        }
        throw new Error("Invalid response format. Apps Script doGet may not be implemented.");
      }
      
      // Format properties (ensure compatibility with camelCase and standard titles)
      const formatted = data.map(item => {
        // Detect if the file name is actually a full URL (e.g. Google Drive link)
        const isUrl = (val) => val && typeof val === 'string' && val.startsWith('http');
        const liveResumeUrl = item.resumeUrl || item["Resume Link"] || item["Resume"] || item["Column_15"] || 
                              item["resumeLink"] || item["resume"] || 
                              (isUrl(item.fileName) ? item.fileName : "") ||
                              (isUrl(item.FileName) ? item.FileName : "") ||
                              (isUrl(item["File Name"]) ? item["File Name"] : "") ||
                              "";
                              
        return {
          Timestamp: item.Timestamp || item.timestamp || new Date().toISOString(),
          firstName: item.firstName || item.FirstName || item["First Name"] || "",
          lastName: item.lastName || item.LastName || item["Last Name"] || "",
          email: item.email || item.Email || item["Email Address"] || "",
          contact: item.contact || item.Contact || item["Contact Number"] || "",
          gender: item.gender || item.Gender || "",
          yearOfGraduation: item.yearOfGraduation || item.YearOfGraduation || item["Graduation Year"] || "",
          experienceInYears: item.experienceInYears || item.ExperienceInYears || item["Experience (Years)"] || "0",
          currentEmployer: item.currentEmployer || item.CurrentEmployer || item["Current Employer"] || "",
          currentCTC: item.currentCTC || item.CurrentCTC || item["Current CTC"] || "",
          expectedCTC: item.expectedCTC || item.ExpectedCTC || item["Expected CTC"] || "",
          noticePeriod: item.noticePeriod || item.NoticePeriod || item["Notice Period"] || "",
          skillSet: item.skillSet || item.SkillSet || item.skillset || item["Technical Skill Set"] || item["Technical Skill Set *"] || item["Technical Skills"] || item["Skill Set"] || item["Skillset"] || item.skills || item.Skills || "",
          source: item.source || item.Source || "",
          currentLocation: item.currentLocation || item.CurrentLocation || item["Current Location"] || "",
          preferredLocation: item.preferredLocation || item.PreferredLocation || item["Preferred Location"] || "",
          fileName: isUrl(item.fileName) ? "View Live Resume" : (item.fileName || item.FileName || item["File Name"] || item["Resume"] || "Resume.pdf"),
          resumeUrl: liveResumeUrl
        };
      });

      setCandidates(formatted);
      setIsDemoMode(false);
      toast.success("Candidate Records Synchronized Live!");
    } catch (err) {
      console.error("Error fetching careers sheet:", err);
      // Automatically fallback to demo mode so user sees the interface
      setCandidates(MOCKUP_CANDIDATES);
      setIsDemoMode(true);
      toast.warning("Could not connect to live sheet. Loaded Demo Environment.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCandidates();
  }, []);

  // Filter candidates based on search query, experience, and location
  const filteredCandidates = candidates.filter(cand => {
    const fullName = `${cand.firstName} ${cand.lastName}`.toLowerCase();
    const email = cand.email.toLowerCase();
    const skills = cand.skillSet.toLowerCase();
    const currentLoc = cand.currentLocation.toLowerCase();
    const preferredLoc = cand.preferredLocation.toLowerCase();
    const term = searchQuery.toLowerCase();

    const matchesSearch = 
      fullName.includes(term) || 
      email.includes(term) || 
      skills.includes(term) || 
      currentLoc.includes(term) ||
      preferredLoc.includes(term);

    // Experience filter logic
    let matchesExp = true;
    const exp = parseInt(cand.experienceInYears) || 0;
    if (experienceFilter === 'entry') matchesExp = exp <= 1;
    else if (experienceFilter === 'mid') matchesExp = exp > 1 && exp <= 4;
    else if (experienceFilter === 'senior') matchesExp = exp >= 5;

    // Location filter logic
    let matchesLoc = true;
    if (locationFilter !== 'all') {
      matchesLoc = cand.preferredLocation.toLowerCase() === locationFilter.toLowerCase() || 
                   cand.currentLocation.toLowerCase() === locationFilter.toLowerCase();
    }

    return matchesSearch && matchesExp && matchesLoc;
  });

  // Extract unique locations for filtering dropdown
  const uniqueLocations = Array.from(
    new Set(
      candidates.flatMap(cand => [cand.currentLocation, cand.preferredLocation])
        .filter(loc => loc && loc.trim() !== '')
    )
  );



  // Export to CSV
  const exportToCSV = () => {
    if (filteredCandidates.length === 0) {
      toast.error("No data available to export");
      return;
    }

    const headers = [
      "Timestamp", "First Name", "Last Name", "Email", "Contact", "Gender", 
      "Graduation Year", "Experience (Years)", "Current Employer", 
      "Current CTC", "Expected CTC", "Notice Period", "Skills", "Source", 
      "Current Location", "Preferred Location", "Resume Filename", "Resume URL"
    ];

    const safeCell = (val) => {
      if (val === null || val === undefined) return '""';
      return `"${String(val).replace(/"/g, '""')}"`;
    };

    const csvRows = [headers.join(",")];

    filteredCandidates.forEach(cand => {
      let dateString = cand.Timestamp;
      try {
        const d = new Date(cand.Timestamp);
        if (!isNaN(d.getTime())) {
          dateString = d.toLocaleString('en-IN');
        }
      } catch (e) {}

      const row = [
        safeCell(dateString),
        safeCell(cand.firstName),
        safeCell(cand.lastName),
        safeCell(cand.email),
        safeCell(cand.contact),
        safeCell(cand.gender),
        safeCell(cand.yearOfGraduation),
        safeCell(cand.experienceInYears),
        safeCell(cand.currentEmployer),
        safeCell(cand.currentCTC),
        safeCell(cand.expectedCTC),
        safeCell(cand.noticePeriod),
        safeCell(cand.skillSet),
        safeCell(cand.source),
        safeCell(cand.currentLocation),
        safeCell(cand.preferredLocation),
        safeCell(cand.fileName),
        safeCell(cand.resumeUrl)
      ];
      csvRows.push(row.join(","));
    });

    const blob = new Blob(["\ufeff" + csvRows.join("\n")], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `iMatrix_Careers_Applicants_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Candidate database exported successfully!");
  };

  // Calculate Metrics
  const totalCount = filteredCandidates.length;
  const avgExp = totalCount > 0 
    ? (filteredCandidates.reduce((acc, curr) => acc + (parseFloat(curr.experienceInYears) || 0), 0) / totalCount).toFixed(1)
    : 0;

  const immediateNoticeCount = filteredCandidates.filter(c => 
    c.noticePeriod.toLowerCase().includes('immediate') || 
    c.noticePeriod.toLowerCase().includes('0 days')
  ).length;

  // Pagination Calculations
  const totalPages = Math.ceil(filteredCandidates.length / itemsPerPage);
  const indexOfLastCandidate = currentPage * itemsPerPage;
  const indexOfFirstCandidate = indexOfLastCandidate - itemsPerPage;
  const currentCandidates = filteredCandidates.slice(indexOfFirstCandidate, indexOfLastCandidate);

  // Helper to generate page numbers with ellipses if needed
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else {
      let start = Math.max(1, currentPage - 2);
      let end = Math.min(totalPages, currentPage + 2);
      if (start === 1) {
        end = maxVisiblePages;
      } else if (end === totalPages) {
        start = totalPages - maxVisiblePages + 1;
      }
      for (let i = start; i <= end; i++) pageNumbers.push(i);
    }
    return pageNumbers;
  };

  return (
    <div className="superadmin-container admin-root">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
      
      {/* ── Admin Header ── */}
      <header className="admin-header">
        <div className="admin-header-title">
          <div className="admin-badge">
            <span className="live-dot animate-pulse" />
            {isDemoMode ? 'Sandbox Environment' : 'Live Data Services'}
          </div>
          <h1>SuperAdmin Portal</h1>
          <p>Global Talent Acquisition Board & Candidate Management Console</p>
        </div>
        
        <div className="admin-actions">
          <button 
            className={`refresh-btn ${loading ? 'loading-spin' : ''}`} 
            onClick={() => fetchCandidates(false)} 
            disabled={loading}
            title="Sync Live with Google Sheet"
          >
            <FaSyncAlt /> <span>Sync Live</span>
          </button>
          
          <button 
            className={`demo-btn ${isDemoMode ? 'active' : ''}`}
            onClick={() => fetchCandidates(true)}
            title="Toggle Demo Mock Data"
          >
            <FaDatabase /> <span>Demo Mode</span>
          </button>

          <button 
            className="export-btn" 
            onClick={exportToCSV}
            title="Export database as CSV"
          >
            <FaDownload /> <span>Export CSV</span>
          </button>
        </div>
      </header>

      {/* ── Sub Navigation Links for Admin Dashboards ── */}
      <nav className="admin-subnav" style={{
        display: 'flex',
        gap: '20px',
        marginBottom: '30px',
        borderBottom: '2px solid var(--border, #e2e8f0)',
        paddingBottom: '12px'
      }}>
        <Link to="/admin/superadmin" style={{
          textDecoration: 'none',
          color: 'var(--brand, #6b7a52)',
          fontWeight: '700',
          fontSize: '1.05rem',
          borderBottom: '3px solid var(--brand, #6b7a52)',
          paddingBottom: '10px',
          transition: 'all 0.2s'
        }}>
          Careers Portal
        </Link>
        <Link to="/admin/contactus" style={{
          textDecoration: 'none',
          color: 'var(--text-secondary, #64748b)',
          fontWeight: '700',
          fontSize: '1.05rem',
          borderBottom: '3px solid transparent',
          paddingBottom: '10px',
          transition: 'all 0.2s'
        }}>
          Contact Messages
        </Link>
        <Link to="/admin/pagehits" style={{
          textDecoration: 'none',
          color: 'var(--text-secondary, #64748b)',
          fontWeight: '700',
          fontSize: '1.05rem',
          borderBottom: '3px solid transparent',
          paddingBottom: '10px',
          transition: 'all 0.2s'
        }}>
          Page Hits Tracker
        </Link>
        <Link to="/admin/whatsnew" style={{
          textDecoration: 'none',
          color: 'var(--text-secondary, #64748b)',
          fontWeight: '700',
          fontSize: '1.05rem',
          borderBottom: '3px solid transparent',
          paddingBottom: '10px',
          transition: 'all 0.2s'
        }}>
          What's New CMS
        </Link>
      </nav>



      {/* ── Key Metrics Cards ── */}
      <section className="metrics-dashboard">
        <div className="metric-card">
          <div className="metric-icon-wrap user-icon">
            <FaUser />
          </div>
          <div className="metric-info">
            <span className="metric-label">Total Applications</span>
            <h2 className="metric-value">{totalCount}</h2>
            <span className="metric-trend green">Showing matching profiles</span>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon-wrap experience-icon">
            <FaBriefcase />
          </div>
          <div className="metric-info">
            <span className="metric-label">Average Experience</span>
            <h2 className="metric-value">{avgExp} Years</h2>
            <span className="metric-trend blue">Diverse technical talent</span>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon-wrap notice-icon">
            <FaSyncAlt />
          </div>
          <div className="metric-info">
            <span className="metric-label">Immediate Joiners</span>
            <h2 className="metric-value">{immediateNoticeCount}</h2>
            <span className="metric-trend gold">Available for review</span>
          </div>
        </div>
      </section>

      {/* ── Filter Controls ── */}
      <section className="filter-controls-card">
        <div className="search-box-wrap">
          <FaSearch className="search-icon" />
          <input 
            type="text" 
            placeholder="Search by candidate name, emails, specific skillsets or locations..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="clear-search" onClick={() => setSearchQuery('')}>
              <FaTimes />
            </button>
          )}
        </div>

        <div className="filters-dropdowns-wrap">
          <div className="filter-select-group">
            <label>Experience</label>
            <select 
              value={experienceFilter} 
              onChange={(e) => setExperienceFilter(e.target.value)}
            >
              <option value="all">All Levels</option>
              <option value="entry">Entry Level (≤ 1 year)</option>
              <option value="mid">Mid-Senior (2 - 4 years)</option>
              <option value="senior">Lead & Architect (≥ 5 years)</option>
            </select>
          </div>

          <div className="filter-select-group">
            <label>Preferred Location</label>
            <select 
              value={locationFilter} 
              onChange={(e) => setLocationFilter(e.target.value)}
            >
              <option value="all">All Regions</option>
              {uniqueLocations.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* ── Table Board ── */}
      <section className="candidates-table-card">
        {loading ? (
          <div className="table-loading-state">
            <FaSyncAlt className="loading-spin" />
            <p>Fetching Candidate Database...</p>
          </div>
        ) : filteredCandidates.length === 0 ? (
          <div className="table-empty-state">
            <FaDatabase />
            <h3>No Candidate Records Found</h3>
            <p>Try modifying your filters or search terms to uncover matches.</p>
          </div>
        ) : (
          <div className="table-responsive-wrapper">
            <table className="candidates-table">
              <thead>
                <tr>
                  <th>Applicant Profile</th>
                  <th>Contact Info</th>
                  <th>Experience</th>
                  <th>Compensation</th>
                  <th>Preferred Hub</th>
                  <th>Technical Core</th>
                  <th className="text-center">Details</th>
                </tr>
              </thead>
              <tbody>
                {currentCandidates.map((cand, index) => {
                  const skillsList = cand.skillSet && cand.skillSet.trim() !== "" ? cand.skillSet.split(',').slice(0, 3) : [];
                  
                  return (
                    <tr key={cand.email + index} onClick={() => setSelectedCandidate(cand)}>
                      <td>
                        <div className="candidate-profile-cell">
                          <div className="candidate-avatar">
                            {cand.firstName[0]}{cand.lastName[0]}
                          </div>
                          <div>
                            <h4 className="candidate-name">{cand.firstName} {cand.lastName}</h4>
                            <span className="candidate-timestamp">
                              {new Date(cand.Timestamp).toLocaleDateString('en-IN', {
                                day: 'numeric', month: 'short', year: 'numeric'
                              })}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="contact-details-cell">
                          <span className="detail-row"><FaEnvelope /> {cand.email}</span>
                          <span className="detail-row"><FaPhone /> {cand.contact}</span>
                        </div>
                      </td>
                      <td>
                        <div className="experience-cell">
                          <span className="exp-badge">
                            {cand.experienceInYears} Years
                          </span>
                          <span className="exp-sub">
                            Grad: {cand.yearOfGraduation}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="compensation-cell">
                          <span className="ctc-current">Current: {cand.currentCTC || 'N/A'}</span>
                          <span className="ctc-expected">Target: {cand.expectedCTC}</span>
                        </div>
                      </td>
                      <td>
                        <div className="location-cell">
                          <span className="loc-pref"><FaMapMarkerAlt /> {cand.preferredLocation}</span>
                          <span className="loc-curr">From: {cand.currentLocation}</span>
                        </div>
                      </td>
                      <td>
                        <div className="skills-cell">
                          {skillsList.length > 0 ? (
                            <>
                              {skillsList.map((skill, sIdx) => (
                                <span key={sIdx} className="skill-tag">{skill.trim()}</span>
                              ))}
                              {cand.skillSet.split(',').length > 3 && (
                                <span className="skill-tag-more">+{cand.skillSet.split(',').length - 3}</span>
                              )}
                            </>
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No skills listed</span>
                          )}
                        </div>
                      </td>
                      <td className="text-center" onClick={(e) => e.stopPropagation()}>
                        <button 
                          className="view-details-btn"
                          onClick={() => setSelectedCandidate(cand)}
                        >
                          Review File
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="table-pagination">
                <div className="pagination-info">
                  Showing <strong>{indexOfFirstCandidate + 1}</strong> to <strong>{Math.min(indexOfLastCandidate, filteredCandidates.length)}</strong> of <strong>{filteredCandidates.length}</strong> candidates
                </div>
                <div className="pagination-buttons">
                  <button 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                    disabled={currentPage === 1}
                    className="pagination-btn prev-btn"
                  >
                    <FaChevronLeft className="pagination-icon" /> Prev
                  </button>
                  
                  {getPageNumbers().map(pageNumber => (
                    <button
                      key={pageNumber}
                      onClick={() => setCurrentPage(pageNumber)}
                      className={`pagination-number-btn ${currentPage === pageNumber ? 'active' : ''}`}
                    >
                      {pageNumber}
                    </button>
                  ))}
                  
                  <button 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
                    disabled={currentPage === totalPages}
                    className="pagination-btn next-btn"
                  >
                    Next <FaChevronRight className="pagination-icon" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* ── Candidate Details Premium Drawer/Modal ── */}
      {selectedCandidate && (
        <div className="modal-overlay" onClick={() => setSelectedCandidate(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <header className="modal-header">
              <div className="modal-title-wrap">
                <div className="candidate-avatar large">
                  {selectedCandidate.firstName[0]}{selectedCandidate.lastName[0]}
                </div>
                <div>
                  <h2>{selectedCandidate.firstName} {selectedCandidate.lastName}</h2>
                  <p className="modal-subtitle">
                    Candidate Profile • {selectedCandidate.experienceInYears} Years Exp
                  </p>
                </div>
              </div>
              <button className="close-modal-btn" onClick={() => setSelectedCandidate(null)}>
                <FaTimes />
              </button>
            </header>

            <div className="modal-body-content">
              
              <div className="modal-info-section">
                <h3><FaUser /> Contact & Basic Details</h3>
                <div className="modal-grid-fields">
                  <div className="field-group">
                    <span className="field-label">Email Address</span>
                    <a href={`mailto:${selectedCandidate.email}`} className="field-value link">
                      {selectedCandidate.email}
                    </a>
                  </div>
                  <div className="field-group">
                    <span className="field-label">Phone Number</span>
                    <a href={`tel:${selectedCandidate.contact}`} className="field-value link">
                      {selectedCandidate.contact}
                    </a>
                  </div>
                  <div className="field-group">
                    <span className="field-label">Gender</span>
                    <span className="field-value">{selectedCandidate.gender || 'Not specified'}</span>
                  </div>
                  <div className="field-group">
                    <span className="field-label">Graduation Year</span>
                    <span className="field-value"><FaGraduationCap /> {selectedCandidate.yearOfGraduation}</span>
                  </div>
                </div>
              </div>

              <div className="modal-info-section">
                <h3><FaBriefcase /> Professional & Compensation Details</h3>
                <div className="modal-grid-fields">
                  <div className="field-group">
                    <span className="field-label">Current Employer</span>
                    <span className="field-value">{selectedCandidate.currentEmployer || 'None / Fresh Graduate'}</span>
                  </div>
                  <div className="field-group">
                    <span className="field-label">Notice Period</span>
                    <span className="field-value">{selectedCandidate.noticePeriod || 'N/A'}</span>
                  </div>
                  <div className="field-group">
                    <span className="field-label">Current CTC</span>
                    <span className="field-value"><FaRupeeSign /> {selectedCandidate.currentCTC || 'N/A'}</span>
                  </div>
                  <div className="field-group">
                    <span className="field-label">Expected CTC</span>
                    <span className="field-value"><FaRupeeSign /> {selectedCandidate.expectedCTC}</span>
                  </div>
                </div>
              </div>

              <div className="modal-info-section">
                <h3><FaMapMarkerAlt /> Geographic & Source Info</h3>
                <div className="modal-grid-fields">
                  <div className="field-group">
                    <span className="field-label">Current Location</span>
                    <span className="field-value">{selectedCandidate.currentLocation}</span>
                  </div>
                  <div className="field-group">
                    <span className="field-label">Preferred Location Hub</span>
                    <span className="field-value">{selectedCandidate.preferredLocation}</span>
                  </div>
                  <div className="field-group">
                    <span className="field-label">Application Channel</span>
                    <span className="field-value">{selectedCandidate.source || 'Direct Application'}</span>
                  </div>
                  <div className="field-group">
                    <span className="field-label">Submission Date</span>
                    <span className="field-value">
                      {new Date(selectedCandidate.Timestamp).toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>
              </div>

              <div className="modal-info-section full-width">
                <h3>Technical Skills Matrix</h3>
                <div className="skills-expanded-wrap">
                  {selectedCandidate.skillSet.split(',').map((skill, sIdx) => (
                    <span key={sIdx} className="skill-tag large">{skill.trim()}</span>
                  ))}
                </div>
              </div>

              <div className="modal-info-section full-width">
                <h3>Document Package</h3>
                <div className="resume-download-card">
                  <div className="resume-icon-wrap">
                    <FaFilePdf />
                  </div>
                  <div className="resume-info" style={{ flex: 1, minWidth: 0 }}>
                    <h4>{selectedCandidate.fileName}</h4>
                    {selectedCandidate.resumeUrl && selectedCandidate.resumeUrl.startsWith('http') ? (
                      <div className="direct-link-container" style={{ marginTop: '8px', wordBreak: 'break-all' }}>
                        <a 
                          href={selectedCandidate.resumeUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{
                            color: '#60a5fa',
                            textDecoration: 'underline',
                            fontSize: '0.85rem',
                            fontWeight: '500',
                            transition: 'color 0.2s',
                          }}
                          onMouseOver={(e) => e.target.style.color = '#93c5fd'}
                          onMouseOut={(e) => e.target.style.color = '#60a5fa'}
                        >
                          {selectedCandidate.resumeUrl}
                        </a>
                      </div>
                    ) : (
                      <p style={{ color: '#9ca3af', margin: '4px 0 0 0', fontSize: '0.85rem' }}>
                        No live resume URL available for this record.
                      </p>
                    )}
                  </div>
                </div>
              </div>

            </div>

            <footer className="modal-footer-actions">
              <a href={`mailto:${selectedCandidate.email}?subject=Your application to iMatrix Technologies`} className="contact-candidate-btn email">
                <FaEnvelope /> Email Candidate
              </a>
              <a href={`tel:${selectedCandidate.contact}`} className="contact-candidate-btn call">
                <FaPhone /> Call Candidate
              </a>
              <button className="dismiss-btn" onClick={() => setSelectedCandidate(null)}>
                Done Reviewing
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdmin;
